import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import * as api from '../../api/PurchaseAPI/directStockUpdationAPI';

const getErrorMessage = (err, fallback) =>
    err?.ErrorDetails?.ErrorMessage || err?.Message || err?.message || fallback;

// Categories 3 and 4 are hidden from this page's category dropdown in legacy —
// replicated here rather than treated as a bug since it's an explicit, deliberate filter.
const EXCLUDED_CATEGORY_IDS = ['3', '4'];

// ── Async Thunks ─────────────────────────────────────────────────────────────

export const fetchDirectStockCCList = createAsyncThunk(
    'directStockUpdation/fetchDirectStockCCList',
    async ({ uid, rid }, { rejectWithValue }) => {
        try {
            const res = await api.getStockupdationCCs(uid, rid);
            return res?.Data || [];
        } catch (err) {
            return rejectWithValue(getErrorMessage(err, 'Failed to load cost centers'));
        }
    }
);

export const fetchDirectStockCategories = createAsyncThunk(
    'directStockUpdation/fetchDirectStockCategories',
    async ({ ccode }, { rejectWithValue }) => {
        try {
            const res = await api.getIndentCategoriesDetails(ccode);
            const list = res?.Data || [];
            return list.filter((c) => !EXCLUDED_CATEGORY_IDS.includes(String(c.Categoryid)));
        } catch (err) {
            return rejectWithValue(getErrorMessage(err, 'Failed to load categories'));
        }
    }
);

export const fetchDirectStockSuggestions = createAsyncThunk(
    'directStockUpdation/fetchDirectStockSuggestions',
    async ({ pfx, typ, cat }, { rejectWithValue }) => {
        try {
            const res = await api.getAutoCompleteDirectStock(pfx, typ, cat);
            return res?.Data || [];
        } catch (err) {
            return rejectWithValue(getErrorMessage(err, 'Failed to load suggestions'));
        }
    }
);

// Success format from the API is "Submited" — anything else is an error message
// meant to be shown verbatim.
export const addDirectStockItem = createAsyncThunk(
    'directStockUpdation/addDirectStockItem',
    async (payload, { rejectWithValue }) => {
        try {
            const res = await api.saveDirectStockItems(payload);
            const status = res?.Data || '';
            if (status !== 'Submited') {
                return rejectWithValue(status || 'Failed to add item');
            }
            return true;
        } catch (err) {
            return rejectWithValue(getErrorMessage(err, 'Failed to add item'));
        }
    }
);

export const fetchDirectStockGrid = createAsyncThunk(
    'directStockUpdation/fetchDirectStockGrid',
    async ({ ccode }, { rejectWithValue }) => {
        try {
            const res = await api.getDirectStockItemDetails(ccode);
            return res?.Data || [];
        } catch (err) {
            return rejectWithValue(getErrorMessage(err, 'Failed to load direct stock items'));
        }
    }
);

export const deleteDirectStockRow = createAsyncThunk(
    'directStockUpdation/deleteDirectStockRow',
    async (payload, { rejectWithValue }) => {
        try {
            const res = await api.deleteDirectStockItem(payload);
            const status = res?.Data || '';
            if (status !== 'Submited') {
                return rejectWithValue(status || 'Failed to delete item');
            }
            return true;
        } catch (err) {
            return rejectWithValue(getErrorMessage(err, 'Failed to delete item'));
        }
    }
);

// Success format is "Submited,<RequestNo>".
export const submitDirectStock = createAsyncThunk(
    'directStockUpdation/submitDirectStock',
    async (payload, { rejectWithValue }) => {
        try {
            const res = await api.saveDirectStock(payload);
            const raw = res?.Data || '';
            const [status, refNo] = raw.split(',');
            if (status !== 'Submited') {
                return rejectWithValue(raw || 'Failed to submit direct stock updation');
            }
            return { refNo };
        } catch (err) {
            return rejectWithValue(getErrorMessage(err, 'Failed to submit direct stock updation'));
        }
    }
);

// ── Slice ────────────────────────────────────────────────────────────────────

const initialState = {
    ccList: [],
    categoryList: [],
    suggestions: [],
    gridItems: [],
    submitResult: null,
    loading: {
        ccList: false,
        categoryList: false,
        suggestions: false,
        add: false,
        grid: false,
        deleteItem: false,
        submit: false,
    },
    errors: {
        ccList: null,
        categoryList: null,
        add: null,
        grid: null,
        deleteItem: null,
        submit: null,
    },
};

const directStockUpdationSlice = createSlice({
    name: 'directStockUpdation',
    initialState,
    reducers: {
        clearCategories:    (s) => { s.categoryList = []; },
        clearSuggestions:   (s) => { s.suggestions = []; },
        clearGrid:          (s) => { s.gridItems = []; s.errors.grid = null; },
        clearSubmitResult:  (s) => { s.submitResult = null; s.errors.submit = null; },
        resetDirectStockUpdation: () => initialState,
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchDirectStockCCList.pending,   (s) => { s.loading.ccList = true; s.errors.ccList = null; })
            .addCase(fetchDirectStockCCList.fulfilled, (s, a) => { s.loading.ccList = false; s.ccList = a.payload; })
            .addCase(fetchDirectStockCCList.rejected,  (s, a) => { s.loading.ccList = false; s.errors.ccList = a.payload; });

        builder
            .addCase(fetchDirectStockCategories.pending,   (s) => { s.loading.categoryList = true; s.errors.categoryList = null; })
            .addCase(fetchDirectStockCategories.fulfilled, (s, a) => { s.loading.categoryList = false; s.categoryList = a.payload; })
            .addCase(fetchDirectStockCategories.rejected,  (s, a) => { s.loading.categoryList = false; s.errors.categoryList = a.payload; s.categoryList = []; });

        builder
            .addCase(fetchDirectStockSuggestions.pending,   (s) => { s.loading.suggestions = true; })
            .addCase(fetchDirectStockSuggestions.fulfilled, (s, a) => { s.loading.suggestions = false; s.suggestions = a.payload; })
            .addCase(fetchDirectStockSuggestions.rejected,  (s) => { s.loading.suggestions = false; s.suggestions = []; });

        builder
            .addCase(addDirectStockItem.pending,   (s) => { s.loading.add = true; s.errors.add = null; })
            .addCase(addDirectStockItem.fulfilled, (s) => { s.loading.add = false; })
            .addCase(addDirectStockItem.rejected,  (s, a) => { s.loading.add = false; s.errors.add = a.payload; });

        builder
            .addCase(fetchDirectStockGrid.pending,   (s) => { s.loading.grid = true; s.errors.grid = null; })
            .addCase(fetchDirectStockGrid.fulfilled, (s, a) => { s.loading.grid = false; s.gridItems = a.payload; })
            .addCase(fetchDirectStockGrid.rejected,  (s, a) => { s.loading.grid = false; s.errors.grid = a.payload; s.gridItems = []; });

        builder
            .addCase(deleteDirectStockRow.pending,   (s) => { s.loading.deleteItem = true; s.errors.deleteItem = null; })
            .addCase(deleteDirectStockRow.fulfilled, (s) => { s.loading.deleteItem = false; })
            .addCase(deleteDirectStockRow.rejected,  (s, a) => { s.loading.deleteItem = false; s.errors.deleteItem = a.payload; });

        builder
            .addCase(submitDirectStock.pending,   (s) => { s.loading.submit = true; s.errors.submit = null; s.submitResult = null; })
            .addCase(submitDirectStock.fulfilled, (s, a) => { s.loading.submit = false; s.submitResult = a.payload; s.gridItems = []; })
            .addCase(submitDirectStock.rejected,  (s, a) => { s.loading.submit = false; s.errors.submit = a.payload; });
    },
});

export const {
    clearCategories,
    clearSuggestions,
    clearGrid,
    clearSubmitResult,
    resetDirectStockUpdation,
} = directStockUpdationSlice.actions;

// ── Selectors ────────────────────────────────────────────────────────────────
export const selectDSUCCList       = (s) => s.directStockUpdationCreation.ccList;
export const selectDSUCategoryList = (s) => s.directStockUpdationCreation.categoryList;
export const selectDSUSuggestions  = (s) => s.directStockUpdationCreation.suggestions;
export const selectDSUGridItems    = (s) => s.directStockUpdationCreation.gridItems;
export const selectDSUSubmitResult = (s) => s.directStockUpdationCreation.submitResult;
export const selectDSULoading      = (s) => s.directStockUpdationCreation.loading;
export const selectDSUErrors       = (s) => s.directStockUpdationCreation.errors;

export default directStockUpdationSlice.reducer;
